import clsx from 'clsx';
import dynamic from 'next/dynamic';

import { JOB_FUNCTION_OPTIONS } from 'bx-forms/eas/getapp';
import LazyContent from 'components/LazyContent';

import classes from './InlineFaas.module.scss';

const MultiFieldInlineForm = dynamic(
  () => import('bx-forms/eas/getapp').then((mod) => mod.MultiFieldInlineForm),
  { ssr: false }
);

const InlineForm = dynamic(
  () => import('bx-forms/eas/getapp').then((mod) => mod.InlineForm),
  { ssr: false }
);

const GATEWAY_URL = 'https://www.capterra.com/bx-capterra-api/bx-forms/submit';

// Field presets for embedded blog forms
const EMAIL_ONLY_FIELDS = [
  { name: 'emailAddress', type: 'email', label: 'Email Address' },
];

const EMAIL_JOB_FUNCTION_FIELDS = [
  { name: 'emailAddress', type: 'email', label: 'Email Address' },
  {
    name: 'jobFunction',
    type: 'select',
    label: 'Job Function',
    options: JOB_FUNCTION_OPTIONS,
  },
];

const EMAIL_NAME_JOB_FUNCTION_FIELDS = [
  { name: 'emailAddress', type: 'email', label: 'Email Address' },
  { name: 'firstName', type: 'text', label: 'First Name' },
  {
    name: 'jobFunction',
    type: 'select',
    label: 'Job Function',
    options: JOB_FUNCTION_OPTIONS,
    fullWidth: true,
  },
];

// Form configurations from Contentful (wt70guc1rpin space)
// Verified 2026-05-19 via MCP query
const BLOG_FORM_CONFIGS = {
  // 3-field forms (email + firstName + jobFunction)
  '6P9kY3ceN5acNjpleWP768': {
    fields: EMAIL_NAME_JOB_FUNCTION_FIELDS,
    headline: '[eBook] True Cost of Choosing the Wrong Software',
    subHeading:
      "Learn about 5 common costly mistakes made during the software selection process, and how to avoid them. Fill out the form below and we'll send a copy of our eBook directly to your inbox.",
    buttonText: 'GET THE EBOOK',
    successHeadline: 'Thank you, enjoy!',
    successMessage: 'Look out for future updates from GetApp.',
    contentCategory: 'Buyer Enablement',
    contentRequest: 'True Cost eBook',
  },
  '2d7hF85vQybAZ9G5xOqmwI': {
    fields: EMAIL_NAME_JOB_FUNCTION_FIELDS,
    headline:
      '[Free Calculator] Marketing and Sales Expenses and Customer Acquisition Cost',
    subHeading:
      "Fill out the form below and we'll send our free calculator template directly to your inbox.",
    buttonText: 'Get the calculator',
    successHeadline: 'Thank you, enjoy!',
    successMessage: 'Look out for future updates from GetApp.',
    contentCategory: 'Marketing',
    contentRequest: 'Customer Acquisition Cost Calculator',
  },
  '2PD6KSCIKTCU2BP4DckzCm': {
    fields: EMAIL_NAME_JOB_FUNCTION_FIELDS,
    headline: '[Free Template] Customer Journey Map',
    subHeading:
      "Optimize your customer journey map to fit your business' needs. Fill out the form below and we'll send our free template directly to your inbox.",
    buttonText: 'GET THE TEMPLATE',
    successHeadline: 'Thank you, enjoy!',
    successMessage: 'Look out for future updates from GetApp.',
    contentCategory: 'Marketing',
    contentRequest: 'Customer Journey Map Template',
  },
  '5CRQMHMH7ynw8tPmPsQiA4': {
    fields: EMAIL_NAME_JOB_FUNCTION_FIELDS,
    headline: 'Assessing your individual level of data literacy',
    subHeading:
      'Download this assessment to gauge the data literacy level of your employees and teams.',
    buttonText: 'Get the assessment',
    successHeadline: 'Thank you, enjoy!',
    successMessage: 'Look out for future updates from GetApp.',
    contentCategory: 'Business intelligence',
    contentRequest: 'Data Literacy Assessment',
  },
  '36Jloh5OfOQTkWht4ONvSb': {
    fields: EMAIL_NAME_JOB_FUNCTION_FIELDS,
    headline: 'Investment Risk Assessment Tool',
    subHeading:
      "Fill out the form below and we'll send you a copy of our investment risk assessment tool.",
    buttonText: 'GET THE TOOL',
    successHeadline: 'Thank you, enjoy!',
    successMessage: 'Look out for future updates from GetApp.',
    contentCategory: 'Buyer Enablement',
    contentRequest: 'Investment Risk Assessment Tool',
  },
  '3eBwGP3gzxETbq4UsINsG0': {
    fields: EMAIL_NAME_JOB_FUNCTION_FIELDS,
    headline: 'Free download: Customer Retention Rate Calculator',
    subHeading:
      'Download this calculator to work out your customer retention rate.',
    buttonText: 'GET THE TEMPLATE',
    successHeadline: 'Thank you, enjoy!',
    successMessage:
      "Your calculator will be delivered to your inbox shortly. If you don't see it in 5 minutes, please check your spam folder.",
    contentCategory: 'CRM',
    contentRequest: 'Customer Retention Calculator',
  },
  NMa8x71s9FxhjZYd0oGno: {
    fields: EMAIL_NAME_JOB_FUNCTION_FIELDS,
    headline:
      '[eBook] Hybrid, Shmybrid: Why Customization, Not Location, Is the Key to Improving the Employee Experience',
    subHeading:
      "Empower employees to customize their employee experience to meet their needs. Fill out the form below and we'll send a copy of our eBook directly to your inbox.",
    buttonText: 'GET THE EBOOK',
    successHeadline: 'Thank you, enjoy!',
    successMessage: 'Look out for future updates from GetApp.',
    contentCategory: 'Human Resources',
    contentRequest: 'Employee Customization eBook',
  },
  DyUnjZvrydghZtgZszGUz: {
    fields: EMAIL_NAME_JOB_FUNCTION_FIELDS,
    headline:
      'Comparison: 4 Popular Open Source and Free Online Hotel Software Options',
    subHeading:
      "Fill out the form below and we'll send a side-by-side comparison of these products directly to your inbox.",
    buttonText: 'GET THE COMPARISON',
    successHeadline: 'Thank you, enjoy!',
    contentCategory: 'Hospitality',
    contentRequest: 'Free Online Hotel Comparison',
  },
  dfPg3smGtOhuL6Ogmn4iN: {
    fields: EMAIL_NAME_JOB_FUNCTION_FIELDS,
    headline:
      'From Boomers to Zoomers: The Characteristics of the Different Generations at Work',
    subHeading:
      'Learn how to bring the generations together at work. Fill out the form below to get a copy of our infographic: From Boomers to Zoomers: Here are the Characteristics of the Different Generations at Work.',
    buttonText: 'GET THE INFOGRAPHIC',
    successHeadline: 'Thank you, enjoy!',
    successMessage: 'Look out for future updates from GetApp.',
    contentCategory: 'Human Resources',
    contentRequest: 'Boomers to Zoomers Infographic',
  },
  '7yUiXAHvGkYPcXCfqkBzb4': {
    fields: EMAIL_NAME_JOB_FUNCTION_FIELDS,
    headline: 'Free Template: Software Vendor Evaluation Chart',
    subHeading:
      "Use this template to help you identify the right software vendor for your business or team. Fill out the form below and we'll send a copy of our template directly to your inbox.",
    buttonText: 'GET THE TEMPLATE',
    successHeadline: 'Thank you, enjoy!',
    successMessage: 'Look out for future updates from GetApp.',
    contentCategory: 'Buyer Enablement',
    contentRequest: 'Vendor Evaluation Template',
  },
  '25QdQXHctpQVMmPO6gfs52': {
    fields: EMAIL_NAME_JOB_FUNCTION_FIELDS,
    headline: 'Send this comparison to my inbox',
    subHeading:
      "Fill out the form below and we'll send a side-by-side comparison of these products directly to your inbox.",
    buttonText: 'GET THE COMPARISON',
    successHeadline: 'Thank you, enjoy!',
    successMessage: 'Look out for future updates from GetApp.',
    contentCategory: 'Project Management',
    contentRequest: 'PM Comparison',
  },
  '1BvyX75kbU8ZqP1apuTWTH': {
    fields: EMAIL_NAME_JOB_FUNCTION_FIELDS,
    headline:
      '[Infographic] The Difference Between an IT Project Manager and Business Program Manager',
    subHeading:
      'Download our infographic to learn about the different roles and responsibilities of IT project managers and business program managers.',
    buttonText: 'GET THE INFOGRAPHIC',
    successHeadline: 'Thank you, enjoy!',
    successMessage: 'Look out for future updates from GetApp.',
    contentCategory: 'IT',
    contentRequest: 'IT PM Infographic',
  },
  '3sxGCXwmzMBHbzq5M6kk1A': {
    fields: EMAIL_NAME_JOB_FUNCTION_FIELDS,
    headline: 'Free Template: Cybersecurity Budget Planning',
    subHeading:
      "Fill out the form below and we'll send you a copy of our cybersecurity budget planning template.",
    buttonText: 'GET THE TEMPLATE',
    successHeadline: 'Thank you, enjoy!',
    successMessage: 'Look out for future updates from GetApp.',
    contentCategory: 'Security',
    contentRequest: 'Cybersecurity Budget Template',
  },

  // 2-field forms (email + jobFunction)
  '4B2MOAsfs20QkTx0zeXg1Y': {
    fields: EMAIL_JOB_FUNCTION_FIELDS,
    fieldLayout: 'vertical',
    headline: "Template: Assess your small business's security posture",
    subHeading:
      'Download this template to assess the security posture of your small business and take corrective actions.',
    buttonText: 'Get the template',
    successHeadline: 'Thank you!',
    successMessage:
      "Your template will be delivered to your inbox shortly. If you don't see an email from GetApp in the next 5 minutes, check your spam folder.",
    contentCategory: 'IT',
    contentRequest: 'Security Assessment Template',
  },
  '5nNGH2756BIywFN7mrXTr6': {
    fields: EMAIL_JOB_FUNCTION_FIELDS,
    headline: 'Worksheet: Lead Magnet Creative Brief',
    subHeading:
      "Download this worksheet to determine what lead magnet to create based on your leads' funnel stage.",
    buttonText: 'Get the worksheet',
    fieldLayout: 'vertical',
    successHeadline: 'Thank you!',
    successMessage:
      "Your worksheet will be delivered to your inbox shortly. If you don't see an email from GetApp in the next 5 minutes, check your spam folder.",
    contentCategory: 'Marketing',
    contentRequest: 'Lead Magnet Creative Brief',
  },
  RKCWzZZJRxMQg115TvHxj: {
    fields: EMAIL_JOB_FUNCTION_FIELDS,
    fieldLayout: 'vertical',
    headline: 'Lead Qualification Worksheet',
    subHeading:
      'Download this worksheet to identify which leads are well-suited to your business and worth going after.',
    buttonText: 'Get the worksheet',
    successHeadline: 'Thank you!',
    successMessage:
      "Your worksheet will be delivered to your inbox shortly. If you don't see an email from GetApp in the next 5 minutes, check your spam folder.",
    contentCategory: 'Sales',
    contentRequest: 'Lead Qualification Worksheet',
  },
  '3aNMiFTbsx2ViODUEd9LIk': {
    fields: EMAIL_JOB_FUNCTION_FIELDS,
    fieldLayout: 'vertical',
    headline: 'PDF: Marketing Email Builder Worksheet',
    subHeading:
      'Download this worksheet to learn how to efficiently craft effective marketing emails.',
    buttonText: 'Get the worksheet now',
    successHeadline: 'Thank you! Enjoy',
    successMessage:
      "Your worksheet will be delivered to your inbox shortly. If you don't see an email from GetApp in the next 5 minutes, check your spam folder.",
    contentCategory: 'Marketing',
    contentRequest: 'Marketing Email Builder Worksheet',
  },
  '69osvKOeyuenGkROyErysu': {
    fields: EMAIL_JOB_FUNCTION_FIELDS,
    fieldLayout: 'vertical',
    headline: 'Assessing your individual level of data literacy',
    subHeading:
      'Download this assessment to gauge the data literacy level of your employees and teams.',
    buttonText: 'Get the assessment',
    successHeadline: 'Thank you!',
    successMessage:
      "Your eBook will be delivered to your inbox shortly. If you don't see it in 5 minutes, please check your spam folder.",
    contentCategory: 'Test',
    contentRequest: 'Test',
  },

  // 1-field forms (email only) - use InlineForm with isSingleStep
  '2BPYslANcNXYnlo6kPS3pn': {
    useInlineForm: true,
    headline: 'Template: Making the case for software purchases',
    subHeading:
      'This PowerPoint template has everything you need to build a business case presentation for your software selection. Fill out the form below to get a copy sent directly to your inbox.',
    buttonText: 'Get the template',
    successHeadline: 'Thank you!',
    successMessage:
      "Your template will be delivered to your inbox shortly. If you don't see an email from GetApp in the next 5 minutes, check your spam folder.",
    contentCategory: 'Blog_Inline',
    contentRequest: 'Software Purchase Proposal Template',
  },
};

/** @type {import("./types").default} */
const InlineFaas = ({ formId, minHeight, pageCategory }) => {
  const config = BLOG_FORM_CONFIGS[formId];

  // Fallback for unknown form IDs - log warning and render nothing
  if (!config) {
    console.warn(
      `[InlineFaas] Unknown formId: ${formId}. Add config to BLOG_FORM_CONFIGS.`
    );
    return null;
  }

  // Use InlineForm for single-field (email only) forms
  if (config.useInlineForm) {
    return (
      <div className={clsx('InlineFaas', classes.root)}>
        <LazyContent minHeight={minHeight}>
          <InlineForm
            formId={formId}
            gatewayUrl={GATEWAY_URL}
            formTemplate="INLINE_CARD"
            isSingleStep={true}
            headline={config.headline}
            subHeading={config.subHeading || ''}
            buttonText={config.buttonText}
            successHeadline={config.successHeadline}
            successMessage={
              config.successMessage ||
              'Your content will be delivered to your inbox shortly.'
            }
            contextProps={{
              pageType: 'blog',
              pagePlacement: 'inline',
              pageCategory,
            }}
            tracking={{
              formName: 'DMBUY_2022_GA_Blog_Acq',
              contentCategory: config.contentCategory || 'Blog_Inline',
              contentRequest: config.contentRequest || config.headline,
            }}
          />
        </LazyContent>
      </div>
    );
  }

  return (
    <div className={clsx('InlineFaas', classes.root)}>
      <LazyContent minHeight={minHeight}>
        <MultiFieldInlineForm
          formId={formId}
          gatewayUrl={GATEWAY_URL}
          fields={config.fields}
          fieldLayout={config.fieldLayout || 'horizontal'}
          headline={config.headline}
          subHeading={config.subHeading || ''}
          buttonText={config.buttonText}
          successHeadline={config.successHeadline}
          successMessage={
            config.successMessage ||
            'Your content will be delivered to your inbox shortly.'
          }
          contextProps={{
            pageType: 'blog',
            pagePlacement: 'inline',
            pageCategory,
          }}
          tracking={{
            formName: 'DMBUY_2022_GA_Blog_Acq',
            contentCategory: config.contentCategory || 'Blog_Inline',
            contentRequest: config.contentRequest || config.headline,
          }}
        />
      </LazyContent>
    </div>
  );
};

export default InlineFaas;
